<template>
  <div>
    <!-- ── Authorization Modal ─────────────────────────────────────────────── -->
    <b-modal
      id="modal-bale-auth"
      ref="myModal"
      centered
      no-close-on-backdrop
      no-close-on-esc
      size="lg"
      hide-footer
      hide-header
    >
      <template #default="{}">
        <div class="modal-inner">
          <!-- Header -->
          <div class="modal-head">
            <div class="d-flex align-items-center" style="gap: 10px">
              <div class="modal-icon" :class="modalIconClass">
                <feather-icon :icon="modalIcon" size="16" />
              </div>
              <div>
                <div class="modal-title">
                  {{
                    modalMode === "bulk"
                      ? `${selectedIds.length} Bale${selectedIds.length > 1 ? "s" : ""} — ${bulkAction === "authorize" ? "Authorize" : "Reject"}`
                      : `Bale ${activeBale ? activeBale.ticket.barcode : ""}`
                  }}
                </div>
                <div v-if="activeBale && modalMode === 'detail'" class="modal-sub">
                  {{ activeBale.source && activeBale.source.name ? activeBale.source.name : "—" }}
                  →
                  {{ activeBale.destination && activeBale.destination.name ? activeBale.destination.name : "—" }}
                  · TF {{ activeBale.transport.number }} · {{ scannerName(activeBale) }}
                </div>
                <div v-if="modalMode === 'bulk'" class="modal-sub">
                  Bulk {{ bulkAction }} — decision will apply to all selected bales
                </div>
              </div>
            </div>
            <feather-icon
              icon="XIcon"
              size="16"
              class="cursor-pointer text-secondary"
              @click="$bvModal.hide('modal-bale-auth')"
            />
          </div>

          <div class="modal-body-inner">
            <!-- Bulk info banner -->
            <b-alert v-if="modalMode === 'bulk'" show variant="info" class="mb-1">
              <div class="alert-body" style="font-size: 12px">
                <strong>{{ selectedIds.length }} bale{{ selectedIds.length > 1 ? "s" : "" }}</strong>
                selected for {{ bulkAction === "authorize" ? "authorization" : "rejection" }}.
                The same reason will be recorded against each bale in the audit log.
              </div>
            </b-alert>

            <!-- Single bale detail -->
            <template v-if="modalMode === 'detail' && activeBale">
              <!-- Info grid -->
              <div class="info-grid mb-1">
                <div class="info-cell">
                  <div class="info-label">Barcode</div>
                  <div class="info-value font-monospace" style="font-size: 13px">
                    {{ activeBale.ticket.barcode }}
                  </div>
                </div>
                <div class="info-cell">
                  <div class="info-label">TF number</div>
                  <div class="info-value font-monospace" style="font-size: 13px">
                    {{ activeBale.transport.number }}
                  </div>
                </div>
                <div class="info-cell">
                  <div class="info-label">Source</div>
                  <div class="info-value">
                    {{ activeBale.source && activeBale.source.name ? activeBale.source.name : "—" }}
                  </div>
                </div>
                <div class="info-cell">
                  <div class="info-label">Destination</div>
                  <div class="info-value">
                    {{ activeBale.destination && activeBale.destination.name ? activeBale.destination.name : "—" }}
                  </div>
                </div>
                <div class="info-cell">
                  <div class="info-label">Scanner</div>
                  <div class="info-value">{{ scannerName(activeBale) }}</div>
                </div>
                <div class="info-cell">
                  <div class="info-label">Status</div>
                  <b-badge :variant="statusVariant(approvedLabel(activeBale.approved))">
                    {{ approvedLabel(activeBale.approved) }}
                  </b-badge>
                </div>
              </div>

              <!-- Weight analysis -->
              <div class="section-head">Weight analysis</div>
              <b-row class="mb-1">
                <b-col cols="6" md="3">
                  <div class="weight-card wc-blue">
                    <div class="wc-label">Purchased</div>
                    <div class="wc-value">
                      {{ activeBale.ticket.mass.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }} kg
                    </div>
                  </div>
                </b-col>
                <b-col cols="6" md="3">
                  <div class="weight-card wc-amber">
                    <div class="wc-label">Previous (dispatched)</div>
                    <div class="wc-value">
                      {{ activeBale.previous.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }} kg
                    </div>
                  </div>
                </b-col>
                <b-col cols="6" md="3">
                  <div class="weight-card wc-green">
                    <div class="wc-label">Current (scanned)</div>
                    <div class="wc-value">
                      {{ activeBale.current.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }} kg
                    </div>
                  </div>
                </b-col>
                <b-col cols="6" md="3">
                  <div class="weight-card" :class="Math.abs(scanVsChecked(activeBale)) > 2 ? 'wc-red' : 'wc-green'">
                    <div class="wc-label">Scan vs previous</div>
                    <div class="wc-value" :class="Math.abs(scanVsChecked(activeBale)) > 2 ? 'text-danger' : 'text-success'">
                      {{ formatVariance(scanVsChecked(activeBale)) }}
                    </div>
                  </div>
                </b-col>
              </b-row>

              <!-- Variance summary cards -->
              <b-row class="mb-1">
                <b-col cols="12" md="6">
                  <div class="variance-card" :class="purchVariance(activeBale) > 0 ? 'vc-warn' : 'vc-danger'">
                    <div class="vc-title" :class="purchVariance(activeBale) > 0 ? 'text-warning' : 'text-danger'">
                      Purchased vs current variance
                    </div>
                    <div class="vc-value" :class="purchVariance(activeBale) > 0 ? 'text-warning' : 'text-danger'">
                      {{ formatVariance(purchVariance(activeBale)) }}
                    </div>
                    <div class="vc-sub">
                      {{ purchVariance(activeBale) > 0 ? "Current weight exceeds purchased" : "Weight lost from purchase" }}
                    </div>
                  </div>
                </b-col>
                <b-col cols="12" md="6">
                  <div class="variance-card" :class="Math.abs(scanVsChecked(activeBale)) > 2 ? 'vc-danger' : 'vc-success'">
                    <div class="vc-title" :class="Math.abs(scanVsChecked(activeBale)) > 2 ? 'text-danger' : 'text-success'">
                      Scan vs previous {{ Math.abs(scanVsChecked(activeBale)) > 2 ? "discrepancy" : "reading" }}
                    </div>
                    <div class="vc-value" :class="Math.abs(scanVsChecked(activeBale)) > 2 ? 'text-danger' : 'text-success'">
                      {{ formatVariance(scanVsChecked(activeBale)) }}
                    </div>
                    <div class="vc-sub">
                      {{ Math.abs(scanVsChecked(activeBale)) > 2 ? "Exceeds 2 kg tolerance" : "Within 2 kg tolerance" }}
                    </div>
                  </div>
                </b-col>
              </b-row>

              <!-- Authorization log -->
              <template v-if="activeBale.authLog && activeBale.authLog.length">
                <div class="section-head">Authorization history</div>
                <div v-for="(entry, idx) in activeBale.authLog" :key="idx" class="log-entry">
                  <b-avatar size="30" :text="initials(entry.user)" variant="light-info" class="flex-shrink-0" />
                  <div class="flex-grow-1 ml-75">
                    <div class="log-name">
                      {{ entry.user }}
                      <span class="log-role">— {{ entry.role }}</span>
                    </div>
                    <div class="log-time">{{ entry.timestamp }}</div>
                    <div class="log-note">{{ entry.note }}</div>
                  </div>
                  <b-badge :variant="entry.action === 'authorized' ? 'light-success' : 'light-danger'" class="ml-50">
                    {{ entry.action.charAt(0).toUpperCase() + entry.action.slice(1) }}
                  </b-badge>
                </div>
              </template>
            </template>

            <!-- ✅ FIX: Use isPending() helper instead of === 0 so string "0" also works -->
            <template v-if="(modalMode === 'detail' && activeBale && isPending(activeBale.approved)) || modalMode === 'bulk'">
              <div class="section-head" :class="{ 'mt-1': modalMode === 'detail' }">
                {{ modalMode === "bulk" ? "Decision reason" : "Authorization decision" }}
              </div>
              <div class="reason-block">
                <label class="reason-label">
                  Reason / notes
                  <span class="required-star">*</span>
                  <span class="reason-hint">(required — recorded in audit log)</span>
                </label>
                <b-form-textarea
                  v-model="authNote"
                  :state="reasonTouched && !authNote.trim() ? false : null"
                  :placeholder="modalMode === 'bulk'
                    ? 'Enter the reason for this bulk decision. It will be recorded against all selected bales...'
                    : 'Enter the reason for your authorization decision. This will be recorded in the audit log and visible to management...'"
                  rows="4"
                  size="sm"
                  @input="reasonTouched = true"
                />
                <div v-if="reasonTouched && !authNote.trim()" class="reason-error-msg">
                  <feather-icon icon="AlertCircleIcon" size="12" class="mr-25" />
                  Please enter a reason before proceeding.
                </div>
              </div>
            </template>
          </div>

          <!-- Footer -->
          <div class="modal-foot">
            <!-- ✅ FIX: Use isPending() helper instead of === 0 -->
            <template v-if="(modalMode === 'detail' && activeBale && isPending(activeBale.approved)) || modalMode === 'bulk'">
              <span class="foot-hint">Reason is mandatory for both authorize and reject.</span>
              <b-button size="sm" variant="outline-secondary" @click="$bvModal.hide('modal-bale-auth')">Cancel</b-button>
              <b-button size="sm" variant="danger" :disabled="saving" @click="handleAction('reject')">
                <b-spinner v-if="saving && pendingAction === 'reject'" small class="mr-50" />
                {{ modalMode === "bulk" ? "Reject all" : "Reject bale" }}
              </b-button>
              <b-button size="sm" variant="success" :disabled="saving" @click="handleAction('authorize')">
                <b-spinner v-if="saving && pendingAction === 'authorize'" small class="mr-50" />
                {{ modalMode === "bulk" ? "Authorize all" : "Authorize bale" }}
              </b-button>
            </template>
            <template v-else>
              <b-button size="sm" variant="primary" @click="$bvModal.hide('modal-bale-auth')">Close</b-button>
            </template>
          </div>
        </div>
      </template>
    </b-modal>
    <!-- ./Authorization Modal -->

    <!-- ── Page header ─────────────────────────────────────────────────────── -->
    <div class="page-section-header mb-1">
      <div>
        <h5 class="mb-25">Bale variance authorization</h5>
        <small class="text-muted">
          Review and authorize bales with weight discrepancies exceeding ±2 kg — mandatory
          reason required for all decisions
        </small>
      </div>
    </div>

    <!-- ── Metric cards ────────────────────────────────────────────────────── -->
    <b-row class="mb-1">
      <b-col cols="6" md="3">
        <b-card class="metric-card metric-card-blue mb-1 mb-md-0">
          <div class="metric-icon metric-icon-blue">
            <feather-icon icon="FileTextIcon" size="16" />
          </div>
          <div class="metric-label">Total bales</div>
          <div class="metric-value text-primary">{{ totalRecords }}</div>
        </b-card>
      </b-col>
      <b-col cols="6" md="3">
        <b-card class="metric-card metric-card-amber mb-1 mb-md-0">
          <div class="metric-icon metric-icon-amber">
            <feather-icon icon="ClockIcon" size="16" />
          </div>
          <div class="metric-label">Pending review</div>
          <div class="metric-value text-warning">{{ meta.pending || 0 }}</div>
        </b-card>
      </b-col>
      <b-col cols="6" md="3">
        <b-card class="metric-card metric-card-green mb-1 mb-md-0">
          <div class="metric-icon metric-icon-green">
            <feather-icon icon="CheckCircleIcon" size="16" />
          </div>
          <div class="metric-label">Authorized</div>
          <div class="metric-value text-success">{{ meta.authorized || 0 }}</div>
        </b-card>
      </b-col>
      <b-col cols="6" md="3">
        <b-card class="metric-card metric-card-red mb-0">
          <div class="metric-icon metric-icon-red">
            <feather-icon icon="XCircleIcon" size="16" />
          </div>
          <div class="metric-label">Rejected</div>
          <div class="metric-value text-danger">{{ meta.rejected || 0 }}</div>
        </b-card>
      </b-col>
    </b-row>

    <!-- ── Main table card ─────────────────────────────────────────────────── -->
    <b-card no-body class="mb-0">
      <!-- Table top controls -->
      <div class="m-2">
        <b-row>
          <b-col cols="12" md="6" class="d-flex align-items-center justify-content-start mb-1 mb-md-0">
            <label>Show</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50 select-size-sm"
            />
            <label>entries</label>
          </b-col>
          <b-col cols="12" md="6">
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                v-model="searchQuery"
                class="d-inline-block mr-1"
                placeholder="Search..."
                size="sm"
              />
            </div>
          </b-col>
        </b-row>
      </div>

      <!-- Bulk action bar -->
      <div v-if="selectedIds.length > 0" class="bulk-bar">
        <feather-icon icon="CheckSquareIcon" size="14" class="mr-50" />
        <span>{{ selectedIds.length }} bale{{ selectedIds.length > 1 ? "s" : "" }} selected</span>
        <b-button size="sm" variant="success" @click="openBulkModal('authorize')">Authorize selected</b-button>
        <b-button size="sm" variant="danger" @click="openBulkModal('reject')">Reject selected</b-button>
        <b-button size="sm" variant="outline-secondary" @click="clearSelection">Clear selection</b-button>
      </div>

      <!-- Table -->
      <b-table
        ref="records"
        striped
        hover
        small
        class="position-relative"
        :items="fetch"
        responsive
        :fields="columns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        empty-text="No records found"
        :sort-desc.sync="isSortDirDesc"
        sticky-header="640px"
      >
        <!-- Checkbox header — supports checked / indeterminate / unchecked -->
        <template #head(selected)>
          <div style="display: flex; align-items: center; justify-content: center">
            <input
              ref="selectAllCheckbox"
              type="checkbox"
              :checked="allPendingOnPageSelected"
              :class="{ 'checkbox-indeterminate': somePendingOnPageSelected }"
              style="width: 15px; height: 15px; cursor: pointer; accent-color: #28a745"
              @change="toggleAll"
            />
          </div>
        </template>

        <!-- ✅ FIX: Use isPending() so string "0" also renders the checkbox -->
        <template #cell(selected)="data">
          <div style="display: flex; align-items: center; justify-content: center">
            <input
              v-if="isPending(data.item.approved)"
              type="checkbox"
              :checked="selectedIds.includes(data.item.id)"
              style="width: 15px; height: 15px; cursor: pointer; accent-color: #28a745"
              @change="toggleRow(data.item.id)"
            />
          </div>
        </template>

        <template #cell(updated_at)="data">
          {{ data.item.updated_at | moment("DD/MM/YYYY") }}
        </template>

        <template #cell(barcode)="data">
          <span class="font-monospace text-muted" style="font-size: 12px">
            {{ data.item.ticket.barcode }}
          </span>
        </template>

        <template #cell(tfNumber)="data">
          <span class="font-monospace text-muted" style="font-size: 12px">
            {{ data.item.transport.number }}
          </span>
        </template>

        <template #cell(purchased)="data">
          <span style="font-weight: 500">
            {{ data.item.ticket.mass.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
          </span>
        </template>

        <template #cell(previous)="data">
          {{ data.item.previous.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
        </template>

        <template #cell(scanned)="data">
          {{ data.item.current.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
        </template>

        <template #cell(variance)="data">
          <span :class="Math.abs(scanVsChecked(data.item)) > 2 ? 'text-danger font-weight-bold' : 'text-muted'">
            {{ formatVariance(scanVsChecked(data.item)) }}
          </span>
          <b-badge
            v-if="Math.abs(scanVsChecked(data.item)) > 2"
            variant="danger"
            class="ml-25"
            style="font-size: 10px; padding: 2px 5px"
          >!</b-badge>
        </template>

        <template #cell(boughtVariance)="data">
          <span :class="purchVariance(data.item) > 0 ? 'text-danger' : 'text-warning'">
            {{ formatVariance(purchVariance(data.item)) }}
          </span>
          <b-badge :variant="purchVariance(data.item) > 0 ? 'light-danger' : 'light-warning'" class="ml-25">
            {{ purchVariance(data.item) > 0 ? "Over" : "Under" }}
          </b-badge>
        </template>

        <template #cell(scanner)="data">
          {{ scannerName(data.item) }}
        </template>

        <template #cell(status)="data">
          <b-badge :variant="statusVariant(approvedLabel(data.item.approved))">
            {{ approvedLabel(data.item.approved) }}
          </b-badge>
        </template>

        <!-- ✅ FIX: Use isPending() so buttons show for both integer 0 and string "0" -->
        <template #cell(action)="data">
          <div class="d-flex align-items-center" style="gap: 4px">
            <template v-if="isPending(data.item.approved)">
              <b-button
                size="sm"
                variant="success"
                style="padding: 2px 9px; font-size: 11px"
                @click="openDetailModal(data.item)"
              >
                Authorize
              </b-button>
              <b-button
                size="sm"
                variant="danger"
                style="padding: 2px 9px; font-size: 11px"
                @click="openDetailModal(data.item)"
              >
                Reject
              </b-button>
            </template>
            <b-button
              size="sm"
              variant="outline-secondary"
              style="padding: 2px 9px; font-size: 11px"
              @click="openDetailModal(data.item)"
            >
              {{ isPending(data.item.approved) ? "Detail" : "View" }}
            </b-button>
          </div>
        </template>
      </b-table>

      <!-- Table footer / pagination -->
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">Showing {{ from }} to {{ to }} of {{ totalRecords }} entries</span>
          </b-col>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="totalRecords"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text><feather-icon icon="ChevronLeftIcon" size="18" /></template>
              <template #next-text><feather-icon icon="ChevronRightIcon" size="18" /></template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BRow, BCol, BCard, BTable, BPagination, BButton,
  BFormSelect, BFormSelectOption, BFormInput, BFormTextarea,
  BSpinner, BAlert, BBadge, BAvatar, BModal,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { ref, computed, watch, onUnmounted } from "@vue/composition-api";
import store from "@/store";
import variancesStoreModule from "@/views/cromis/variances/variancesStoreModule";
import useVariancesList from "@/views/cromis/variances/useVariancesList";

export default {
  name: "BaleVarianceAuthorization",

  components: {
    BRow, BCol, BCard, BTable, BPagination, BButton,
    BFormSelect, BFormSelectOption, BFormInput, BFormTextarea,
    BSpinner, BAlert, BBadge, BAvatar, BModal, vSelect,
  },

  setup(props, context) {
    const CROMIS_STORE_MODULE_NAME = "cromis-variances";

    if (!store.hasModule(CROMIS_STORE_MODULE_NAME)) {
      store.registerModule(CROMIS_STORE_MODULE_NAME, variancesStoreModule);
    }
    onUnmounted(() => {
      if (store.hasModule(CROMIS_STORE_MODULE_NAME)) {
        store.unregisterModule(CROMIS_STORE_MODULE_NAME);
      }
    });

    const {
      fetch, refetch,
      columns: composableColumns,
      perPage, currentPage, totalRecords, from, to, meta,
      perPageOptions, searchQuery, sortBy, isSortDirDesc,
      records, resolveStatusVariant,
    } = useVariancesList();

    const columns = computed(() => {
      const base = Array.isArray(composableColumns)
        ? composableColumns
        : composableColumns.value || [];
      const filtered = base.filter((c) => c.key !== "selected");
      return [
        { key: "selected", label: "", sortable: false, thStyle: "width:40px; min-width:40px; text-align:center" },
        ...filtered,
      ];
    });

    // ── Modal state ──────────────────────────────────────────────────────────
    const saving = ref(false);
    const pendingAction = ref(null);
    const authNote = ref("");
    const reasonTouched = ref(false);
    const activeBale = ref(null);
    const modalMode = ref("detail");
    const bulkAction = ref(null);
    const selectedIds = ref([]);

    // ✅ THE FIX — single source of truth for "is this bale pending?"
    // Coerces both integer 0 and string "0" to the same check.
    function isPending(approved) {
      return Number(approved) === 0;
    }
    function isAuthorized(approved) {
      return Number(approved) === 1;
    }
    function isRejected(approved) {
      return Number(approved) === -1;
    }

    const modalIcon = computed(() => {
      if (modalMode.value === "bulk")
        return bulkAction.value === "authorize" ? "CheckCircleIcon" : "XCircleIcon";
      if (!activeBale.value) return "AlertCircleIcon";
      if (isAuthorized(activeBale.value.approved)) return "CheckCircleIcon";
      if (isRejected(activeBale.value.approved)) return "XCircleIcon";
      return "AlertCircleIcon";
    });

    const modalIconClass = computed(() => {
      if (modalMode.value === "bulk")
        return bulkAction.value === "authorize" ? "modal-icon-success" : "modal-icon-danger";
      if (!activeBale.value) return "modal-icon-warning";
      if (isAuthorized(activeBale.value.approved)) return "modal-icon-success";
      if (isRejected(activeBale.value.approved)) return "modal-icon-danger";
      return "modal-icon-warning";
    });

    // ── Bulk selection ───────────────────────────────────────────────────────
    const pagePendingIds = computed(() => {
      if (!records.value || !records.value.length) return [];
      // ✅ FIX: use isPending() here too
      return records.value.filter((b) => isPending(b.approved)).map((b) => b.id);
    });

    const allPendingOnPageSelected = computed(() => {
      if (!pagePendingIds.value.length) return false;
      return pagePendingIds.value.every((id) => selectedIds.value.includes(id));
    });

    const somePendingOnPageSelected = computed(() => {
      if (!pagePendingIds.value.length) return false;
      const checkedCount = pagePendingIds.value.filter((id) => selectedIds.value.includes(id)).length;
      return checkedCount > 0 && checkedCount < pagePendingIds.value.length;
    });

    function toggleAll() {
      if (allPendingOnPageSelected.value) {
        selectedIds.value = selectedIds.value.filter((id) => !pagePendingIds.value.includes(id));
      } else {
        pagePendingIds.value.forEach((id) => {
          if (!selectedIds.value.includes(id)) selectedIds.value.push(id);
        });
      }
    }

    function toggleRow(id) {
      const idx = selectedIds.value.indexOf(id);
      if (idx > -1) selectedIds.value.splice(idx, 1);
      else selectedIds.value.push(id);
    }

    function clearSelection() {
      selectedIds.value = [];
    }

    watch(currentPage, () => { selectedIds.value = []; });

    const selectAllCheckbox = ref(null);
    watch([allPendingOnPageSelected, somePendingOnPageSelected], () => {
      if (selectAllCheckbox.value) {
        selectAllCheckbox.value.indeterminate = somePendingOnPageSelected.value;
      }
    });

    // ── Weight helpers ────────────────────────────────────────────────────────
    function scanVsChecked(b) {
      return +(b.previous - b.current).toFixed(2);
    }
    function purchVariance(b) {
      return +(b.ticket.mass - b.current).toFixed(2);
    }
    function formatVariance(v) {
      return (v > 0 ? "+" : "") + parseFloat(v).toFixed(2) + " kg";
    }

    // ── Display helpers ───────────────────────────────────────────────────────
    function statusVariant(status) {
      if (status === "Authorized") return "light-success";
      if (status === "Rejected") return "light-danger";
      return "light-warning";
    }

    function approvedLabel(approved) {
      if (isAuthorized(approved)) return "Authorized";
      if (isRejected(approved)) return "Rejected";
      return "Pending";
    }

    function scannerName(item) {
      if (!item.scanner || !item.scanner.person) return "—";
      const p = item.scanner.person;
      return p.middleName
        ? `${p.firstName} ${p.middleName} ${p.lastName}`
        : `${p.firstName} ${p.lastName}`;
    }

    function initials(name) {
      return name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
    }

    // ── Reason validation ─────────────────────────────────────────────────────
    function validateReason() {
      reasonTouched.value = true;
      return !!authNote.value.trim();
    }

    // ── Modal open ────────────────────────────────────────────────────────────
    function openDetailModal(item) {
      if (!item.authLog) item.authLog = [];
      activeBale.value = item;
      modalMode.value = "detail";
      authNote.value = "";
      reasonTouched.value = false;
      context.root.$bvModal.show("modal-bale-auth");
    }

    function openBulkModal(action) {
      bulkAction.value = action;
      modalMode.value = "bulk";
      authNote.value = "";
      reasonTouched.value = false;
      context.root.$bvModal.show("modal-bale-auth");
    }

    // ── Authorization action ──────────────────────────────────────────────────
    async function handleAction(action) {
      if (!validateReason()) return;

      const approvedValue = action === "authorize" ? 1 : -1;
      const note = authNote.value.trim();

      let itemsToProcess = [];
      if (modalMode.value === "bulk") {
        itemsToProcess = (records.value || []).filter((b) => selectedIds.value.includes(b.id));
      } else {
        itemsToProcess = [activeBale.value];
      }

      const isBulk = itemsToProcess.length > 1;
      const actionLabel = action === "authorize" ? "Authorize" : "Reject";
      const actionPast = action === "authorize" ? "authorized" : "rejected";
      const confirmColor = action === "authorize" ? "btn btn-success" : "btn btn-danger";

      const confirm = await context.root.$swal({
        title: `${actionLabel} ${isBulk ? itemsToProcess.length + " bales" : "this bale"}?`,
        html: `<div style="font-size:13px;color:#5e5873;text-align:left;margin-top:8px">
          <strong>Action:</strong> ${actionLabel}<br/>
          <strong>Bales:</strong> ${itemsToProcess.length}<br/>
          <strong>Reason:</strong> ${note}
        </div>`,
        icon: action === "authorize" ? "question" : "warning",
        showCancelButton: true,
        confirmButtonText: `Yes, ${actionLabel.toLowerCase()}`,
        cancelButtonText: "Cancel",
        customClass: { confirmButton: confirmColor, cancelButton: "btn btn-outline-secondary ml-1" },
        buttonsStyling: false,
      });

      if (!confirm.isConfirmed) return;

      pendingAction.value = action;
      saving.value = true;

      console.group(`[BaleVariance] ${actionLabel.toUpperCase()} — ${itemsToProcess.length} bale${itemsToProcess.length > 1 ? "s" : ""}`);
      itemsToProcess.forEach((b, i) => {
        console.log(`  [${i + 1}] PUT /variations/${b.id}`, { approved: approvedValue, remarks: note });
      });
      console.groupEnd();

      try {
        await Promise.all(
          itemsToProcess.map((b) =>
            store.dispatch("cromis-variances/update", {
              id: b.id,
              data: { approved: approvedValue, remarks: note },
            })
          )
        );

        context.root.$bvModal.hide("modal-bale-auth");
        clearSelection();
        refetch();

        context.root.$swal({
          icon: action === "authorize" ? "success" : "warning",
          title: `${isBulk ? itemsToProcess.length + " bales" : "Bale"} ${actionPast}`,
          text: `${itemsToProcess.length} bale${itemsToProcess.length > 1 ? "s" : ""} ${actionPast} successfully.`,
          showConfirmButton: false,
          timer: 2500,
        });
      } catch (error) {
        console.error("[BaleVariance] API error:", error);
        const msg = error.response?.status === 422
          ? Object.values(error.response.data.errors).flat().join("\n")
          : "Something went wrong. Please contact technical support.";
        context.root.$swal({
          icon: "error",
          title: error.response?.status === 422 ? "Validation error" : "Server error",
          text: msg,
          showConfirmButton: true,
          customClass: { confirmButton: "btn btn-danger" },
          buttonsStyling: false,
        });
      } finally {
        saving.value = false;
        pendingAction.value = null;
      }
    }

    return {
      fetch, refetch, columns,
      perPage, currentPage, totalRecords, from, to, meta,
      perPageOptions, searchQuery, sortBy, isSortDirDesc,
      records, resolveStatusVariant,
      saving, pendingAction, authNote, reasonTouched,
      activeBale, modalMode, bulkAction, modalIcon, modalIconClass,
      selectedIds, allPendingOnPageSelected, somePendingOnPageSelected,
      pagePendingIds, selectAllCheckbox,
      toggleAll, toggleRow, clearSelection,
      openDetailModal, openBulkModal, handleAction,
      isPending, isAuthorized, isRejected,
      scanVsChecked, purchVariance, formatVariance,
      statusVariant, approvedLabel, scannerName, initials,
    };
  },
};
</script>

<style scoped lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

.page-section-header {
  padding-bottom: 0.85rem;
  border-bottom: 0.5px solid #ebe9f1;
}

// ── Metric cards ───────────────────────────────────────────────────────────
.metric-card {
  border-radius: 0.6rem;
  border-top: 3px solid transparent;
  box-shadow: 0 4px 18px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.05);
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 8px 28px rgba(0,0,0,0.12), 0 2px 6px rgba(0,0,0,0.06);
  }

  ::v-deep .card-body { padding: 1rem 1.1rem; }

  &.metric-card-blue  { border-top-color: #185fa5; }
  &.metric-card-amber { border-top-color: #ef9f27; }
  &.metric-card-green { border-top-color: #639922; }
  &.metric-card-red   { border-top-color: #e24b4a; }

  .metric-icon {
    width: 34px; height: 34px; border-radius: 8px;
    display: flex; align-items: center; justify-content: center;
    margin-bottom: 10px;
    &.metric-icon-blue  { background: #e6f1fb; color: #185fa5; }
    &.metric-icon-amber { background: #faeeda; color: #854f0b; }
    &.metric-icon-green { background: #eaf3de; color: #3b6d11; }
    &.metric-icon-red   { background: #fcebeb; color: #a32d2d; }
  }

  .metric-label {
    font-size: 11px; color: #b9b9c3;
    text-transform: uppercase; letter-spacing: 0.35px; margin-bottom: 5px;
  }
  .metric-value { font-size: 24px; font-weight: 500; line-height: 1.2; }
}

// ── Bulk bar ───────────────────────────────────────────────────────────────
.bulk-bar {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  padding: 9px 16px;
  background: #eaf3de; border-bottom: 0.5px solid #c0dd97;
  font-size: 12px; color: #27500a;
}

// ── Modal ──────────────────────────────────────────────────────────────────
.modal-inner { overflow: hidden; }

.modal-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 0.5px solid #ebe9f1; background: #fafafa;

  .modal-icon {
    width: 34px; height: 34px; border-radius: 8px;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    &.modal-icon-success { background: #eaf3de; color: #3b6d11; }
    &.modal-icon-danger  { background: #fcebeb; color: #a32d2d; }
    &.modal-icon-warning { background: #faeeda; color: #854f0b; }
  }

  .modal-title { font-size: 14px; font-weight: 600; color: #5e5873; }
  .modal-sub   { font-size: 11px; color: #b9b9c3; margin-top: 1px; }
}

.modal-body-inner { padding: 18px 18px 4px; }

.modal-foot {
  display: flex; align-items: center; justify-content: flex-end; gap: 8px;
  padding: 12px 18px;
  border-top: 0.5px solid #ebe9f1; background: #fafafa;
  .foot-hint { flex: 1; font-size: 11px; color: #b9b9c3; }
}

// ── Info grid ──────────────────────────────────────────────────────────────
.info-grid {
  display: grid; grid-template-columns: 1fr 1fr;
  border: 0.5px solid #ebe9f1; border-radius: 8px; overflow: hidden;

  .info-cell {
    padding: 10px 14px;
    border-right: 0.5px solid #ebe9f1; border-bottom: 0.5px solid #ebe9f1;
    &:nth-child(2n)      { border-right: none; }
    &:nth-last-child(-n+2) { border-bottom: none; }
  }
  .info-label {
    font-size: 10px; text-transform: uppercase;
    letter-spacing: 0.4px; color: #b9b9c3; margin-bottom: 3px;
  }
  .info-value { font-size: 13px; font-weight: 500; color: #5e5873; }
}

.section-head {
  font-size: 11px; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.5px; color: #b9b9c3;
  padding-bottom: 8px; border-bottom: 0.5px solid #ebe9f1; margin-bottom: 12px;
}

// ── Weight cards ───────────────────────────────────────────────────────────
.weight-card {
  border-radius: 8px; padding: 11px 13px; margin-bottom: 10px;
  border-left: 3px solid transparent; background: #f8f8f8;
  &.wc-blue  { border-left-color: #185fa5; }
  &.wc-amber { border-left-color: #ef9f27; }
  &.wc-green { border-left-color: #639922; }
  &.wc-red   { border-left-color: #e24b4a; }
  .wc-label {
    font-size: 10px; text-transform: uppercase;
    letter-spacing: 0.3px; color: #b9b9c3; margin-bottom: 4px;
  }
  .wc-value { font-size: 17px; font-weight: 500; color: #5e5873; }
}

// ── Variance cards ─────────────────────────────────────────────────────────
.variance-card {
  border-radius: 8px; padding: 12px 14px; border: 0.5px solid; margin-bottom: 10px;
  &.vc-warn    { background: #faeeda; border-color: #fac775; }
  &.vc-danger  { background: #fcebeb; border-color: #f7c1c1; }
  &.vc-success { background: #eaf3de; border-color: #c0dd97; }
  .vc-title { font-size: 11px; font-weight: 500; margin-bottom: 3px; }
  .vc-value { font-size: 16px; font-weight: 500; }
  .vc-sub   { font-size: 11px; margin-top: 3px; opacity: 0.8; color: inherit; }
}

// ── Auth log ───────────────────────────────────────────────────────────────
.log-entry {
  display: flex; align-items: flex-start; gap: 10px;
  padding: 10px 0; border-bottom: 0.5px solid #ebe9f1;
  &:last-child { border-bottom: none; }
  .log-name { font-size: 12px; font-weight: 600; color: #5e5873; }
  .log-role { font-size: 11px; font-weight: 400; color: #b9b9c3; }
  .log-time { font-size: 11px; color: #b9b9c3; margin-top: 1px; }
  .log-note { font-size: 12px; color: #6e6b7b; margin-top: 3px; }
}

// ── Reason field ───────────────────────────────────────────────────────────
.reason-label {
  display: flex; align-items: center; gap: 4px;
  font-size: 12px; font-weight: 500; color: #5e5873; margin-bottom: 6px;
}
.required-star { color: #a32d2d; font-size: 14px; line-height: 1; }
.reason-hint   { font-size: 11px; font-weight: 400; color: #b9b9c3; }
.reason-error-msg {
  display: flex; align-items: center; gap: 4px;
  font-size: 11px; color: #a32d2d; margin-top: 5px;
}

table { font-size: 13px; }

// ── Checkbox column ────────────────────────────────────────────────────────
::v-deep table thead th:first-child,
::v-deep table tbody td:first-child {
  text-align: center; vertical-align: middle;
  padding-left: 12px; padding-right: 12px;
}
.checkbox-indeterminate { accent-color: #28a745; }
</style>
